/*
 * ==============================================================================
 *
 * Copyright (c) 2023. Koninklijke Philips Electronics N.V.
 *
 * All rights are reserved. Reproduction in whole or in part is prohibited without the prior written consent of the copyright holder.
 *
 * ==============================================================================
 */

import React, { useContext, useState } from "react";
import fetchHelper from "../../services/fetchHelper";
import { Loading } from "./AllReports";
import savingspinner from "../../assets/esign-ui-icons/saving-ico-white.svg";
import { downloadAsBlob, getSafeIrFilename } from "../../utils/helpers";
import CheckboxTableRow from "./CheckboxTableRow";
import { ExportListContext, ViewTabIds } from ".";
import useFeatureFlag from "../../utils/featureFlag";
import { BANNER_BILLING_REPORT_ENABLED } from "../../constants/AppConfigConstants";

export const Regenerate = ({ item }) => {
  const [isLoading, setIsLoading] = useState(false);

  const regenerateReport = async (id) => {
    try {
      setIsLoading(true);
      await fetchHelper.put(`/patientmanagement/rest/interrogations/${id}/regenerate`);
      setIsLoading(false);
    } catch (error) {
      setIsLoading(false);
      console.log("ERROR", error);
    }
  };

  return (
    <button
      className="btn btn-light"
      onClick={() => regenerateReport(item.id)}
      disabled={isLoading}
      style={{ position: "relative", minWidth: 140 }}
    >
      {isLoading ? (
        <div
          id="results-loading-icon"
          className="saving-white-ico icon-64"
          style={{ width: 15, height: 15, marginRight: 3, position: "absolute", left: "15px" }}
        />
      ) : null}
      <span style={{ position: "absolute", right: 24 }}>Regenerate</span>
    </button>
  );
};

const DownloadTable = ({ data, initialLoad, tab }) => {
  const { checkedItems, setCheckedItems, markedAsDownloaded, setMarkedAsDownloaded } =
    useContext(ExportListContext);
  const [downloadingPreReport, setDownloadingPreReport] = useState(null);
  const { featureCheck } = useFeatureFlag();

  const onMarkAsDownloaded = async (item) => {
    try {
      setMarkedAsDownloaded((prev) => [...prev, item.id]);
      await fetchHelper.put("/patientmanagement/rest/interrogations/" + item.id, {
        exported: true,
        id: item.id,
      });
    } catch (error) {
      console.log(error);
    }
  };

  const downloadReport = async (item, patient) => {
    setDownloadingPreReport(item.id);
    const filename = getSafeIrFilename(patient, item.id);
    const downloadUrl = await getDownloadUrl(item);
    await downloadAsBlob(downloadUrl, `${filename}.pdf`);
    setDownloadingPreReport(null);
  };

  const getDownloadUrl = async (item) => {
    const pdfQueueMicroservice = await fetchHelper.getPdfQueueStatus(item.location.id);
    let extraParams = pdfQueueMicroservice ? `/${item.interrogationStatus}` : "";
    return `/patientmanagement/rest/interrogations/${item.id}/download${extraParams}`;
  };

  const onDownloadZip = async (item) => {
    const { content } = await fetchHelper.get({
      url: `/patientmanagement/rest/billingweekly/${item.externalId}`,
    });

    const source = "data:application/octet-stream;base64," + content;
    const downloadLink = document.createElement("a");
    const fileName = `${item.location.name}_${new Date(item.weeklyDate).toLocaleDateString()}.${item.type}`;
    downloadLink.href = source;
    downloadLink.download = fileName;
    downloadLink.click();
  };

  const dateDisplay = (data) => {
    const date = new Date(data);
    return `${date.getMonth() + 1}/${date.getDate()}`;
  };

  const getBannerBilling = () => {
    return featureCheck(BANNER_BILLING_REPORT_ENABLED) ? (
      <div className="banner-billing-weekly alert">
        <p>
          <b>ATTENTION – Important Billing Update</b>
        </p>
        <p>
          Effective January 1st, 2024, CMS has introduced changes to CPT codes for remote monitoring of 30-day
          devices. As we adapt our system to comply with these adjustments, interim measures are in place to
          ensure accurate Billing Reports.
        </p>
        <p>
          <b>Action Required:</b>
        </p>
        <ul>
          <li>
            <b>Split Bill Customers (i.e. IDM bills technical, you bill professional):</b>
            <ul>
              <li>Download and use the report directly from the IDM platform.</li>
            </ul>
          </li>
          <li>
            <b>Non-Split Bill Customers (i.e. IDM invoices you directly):</b>
            <ul>
              <li className="underline">
                <b>DO NOT DOWNLOAD THE REPORT.</b>
              </li>
              <li>Your report will be emailed to you directly by our Customer Success team.</li>
            </ul>
          </li>
        </ul>
        <p>
          <b>Uncertain about your status?</b> Reach out to Customer Success at
          <a href="mailto: customersuccess@philips.com"> customersuccess@philips.com</a> for clarification.
        </p>
      </div>
    ) : null;
  };

  return (
    <div className="contentTbl">
      {tab === ViewTabIds.WEEKLY ? getBannerBilling() : null}
      <table className="overview" id="data-table">
        <thead>
          {tab === ViewTabIds.WEEKLY ? (
            <tr>
              <th style={{ textAlign: "left" }}>Location</th>
              <th style={{ textAlign: "left" }}>Date</th>
              <th style={{ textAlign: "left" }}>Type</th>
              <th style={{ textAlign: "left" }}></th>
            </tr>
          ) : (
            <tr>
              {tab == ViewTabIds.DOWNLOAD && (
                <th>
                  <CheckboxTableRow
                    checkedItems={checkedItems}
                    setCheckedItems={setCheckedItems}
                    data={data}
                  />
                </th>
              )}
              <th style={{ textAlign: "left" }}>Last Name, First</th>
              <th style={{ textAlign: "left" }}>Date</th>
              <th style={{ textAlign: "left" }}>Signed On</th>
              <th style={{ textAlign: "left" }}>Download</th>
              <th style={{ textAlign: "left" }}></th>
              <th style={{ textAlign: "left" }}></th>
            </tr>
          )}
        </thead>

        {initialLoad ? (
          <Loading />
        ) : data.length > 0 ? (
          tab === ViewTabIds.WEEKLY ? (
            <tbody>
              {data.map((item) => (
                <tr key={item.id}>
                  <td style={{ textAlign: "left" }}>{item.location.name}</td>
                  <td style={{ textAlign: "left" }}>{new Date(item.weeklyDate).toLocaleDateString()}</td>
                  <td style={{ textAlign: "left" }}>{item.type}</td>
                  <td style={{ textAlign: "left" }}>
                    <button className="btn btnSuccess" onClick={() => onDownloadZip(item)}>
                      Download
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          ) : (
            <tbody>
              {data.map(({ patient, ...item }) => (
                <tr key={item.id}>
                  {tab == ViewTabIds.DOWNLOAD && (
                    <td>
                      <CheckboxTableRow
                        checkedItems={checkedItems}
                        setCheckedItems={setCheckedItems}
                        data={data}
                        id={item.id}
                      />
                    </td>
                  )}
                  <td style={{ textAlign: "left" }}>
                    {patient?.family}, {patient?.name}
                  </td>
                  <td style={{ textAlign: "left" }}>{dateDisplay(item.createdAt)}</td>
                  <td style={{ textAlign: "left" }}>{new Date(item.signDate).toLocaleDateString()}</td>
                  <td style={{ textAlign: "left" }}>
                    <button className="btn btnSuccess" onClick={() => downloadReport(item, patient)}>
                      {downloadingPreReport == item.id ? (
                        <img id="loading-spinner" src={savingspinner} style={{ width: 15 }}></img>
                      ) : (
                        <span style={{ width: 15 }}></span>
                      )}
                      Download
                    </button>
                  </td>
                  <td style={{ textAlign: "right" }}>
                    <button
                      className="btn btnTeal"
                      onClick={() => onMarkAsDownloaded(item)}
                      disabled={
                        markedAsDownloaded.findIndex((i) => i === item.id) !== -1 || tab === "downloaded"
                      }
                    >
                      {markedAsDownloaded.findIndex((i) => i === item.id) !== -1 || tab === "downloaded"
                        ? `Marked as Downloaded`
                        : `Mark as Downloaded`}
                    </button>
                  </td>
                  <td style={{ textAlign: "right" }}>
                    <Regenerate item={item} />
                  </td>
                </tr>
              ))}
            </tbody>
          )
        ) : null}
      </table>
      {data.length === 0 && !initialLoad ? (
        <div
          style={{
            width: "100%",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: 300,
            fontSize: 18,
          }}
        >
          No reports
        </div>
      ) : null}
    </div>
  );
};

export default DownloadTable;
